import React, { useEffect, useRef, useState } from 'react';
import { TouchableOpacity, View } from 'react-native';
import { translate } from '../../../services/translate';
import FormInput from '../../../components/FormInput';
import { dispatchService } from '../../../services/central-api/dispatch';
import { createStyleSheet, useStyles } from 'react-native-unistyles';
import Toast from 'react-native-toast-message';
import moment from 'moment';
import TagsDropdown from './TagsDropdown';
import MyAppText from '../../../components/MyAppText';
import MyDateRangePicker from '../../../components/MyDateRangePicker';
import { Controller, useForm, useWatch } from 'react-hook-form';
import FileFormatDropdown from '../../../components/FileFormatDropdown';
import { useAuth } from '../../../context/Auth';
import Filters, { FilterSection, FiltersRef } from '../../../components/Filter/FilterIndex/FilterIndex';
import CollapsibleFilter from '../../../components/Filters/Dispatch/CollapsibleFilter';
import ActingBodiesList from '../../../components/Filters/Dispatch/ActingBodiesList';
import FilterDependencyMessage from '../../../components/Filters/FilterDependencyMessage';
import ActingBodyUnitsList from '../../../components/Filters/Dispatch/ActingBodyUnitsList';
import DispatchActingBodiesCommandsList from '../../../components/Filters/Dispatch/DispatchActingBodiesCommandsList';
import PriorityList from '../../../components/Filters/Dispatch/PriorityList';
import StatusList from '../../../components/Filters/Dispatch/StatusList';
import TagsList from '../../../components/Filters/Dispatch/TagsList';
import FilterInput from '../../../components/Filter/components/FilterInput';
import DispatchUnitNamesList from '../../../components/Filters/Dispatch/DispatchUnitNamesList';
import DispatchDisplacedReasonsList from '../../../components/Filters/Dispatch/DispatchDisplacedReasonsList';
import DispatchRegionsList from '../../../components/Filters/RegionsList';
import LocationSelector from '../../../components/LocationSelector';
import Tooltip from '../../../components/Tooltip';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faXmark, IconDefinition } from '@fortawesome/free-solid-svg-icons';
import { Slider } from '@miblanchard/react-native-slider';
import Map from '../../../components/Map';
import FilterRadioList from '../../../components/Filters/FilterRadioList';
import TypeDropdown from '../../../components/Filters/Dispatch/TypeDropdown';
import CameraTagsList from '../../../components/Filters/Dispatch/CameraTagsList';
import DispatchGenericUserList from '../../../components/Filters/Dispatch/DispatchGenericUserList';
import FinishReasonList from '../../../components/Filters/Dispatch/FinishReasonList';
import FinishReasonSubCategoryList from '../../../components/Filters/Dispatch/FinishReasonSubcategoryList';
import OccurrenceTypesList from '../../../components/Filters/Dispatch/OccurrenceTypesList';
import InvolvedPeopleList from '../../../components/Filters/Dispatch/InvolvedPeopleList';
import AttachmentTypesList, { AttachmentType } from '../../../components/Filters/Dispatch/AttachmentTypesList';
import SelectedItemsList from '../../../components/Filters/Mocks/SelectedItemsList';
import { makeSetStateHandler } from '../../../components/helpers/form';

type ValuePiece = Date | null;

interface Props {
    onClose: () => void;
    onApplyFilters: () => void;
    report?: ReportOccurrence;
    canDownloadReport?: boolean;
    downloadReport?: () => void;
}

interface ReportModalForm {
    period: { from: ValuePiece; to: ValuePiece; };
    name: string;
    reportFormat: 'csv' | 'xlsx' | 'pdf';
    tags: number[];
    actingBodyId: number;
    assumedBy: number;
    receivedBy: number;
    transferredBy: number;
    finishedBy: number;
    actingBodyCommandIds: number[];
    triggerTypes: (TriggerType | 'manual')[];
    occurrenceTypes: number[];
    occurrenceTagsIds: number[];
    unitsIds: number[];
    priority: OccurrencePriorities | 'all';
    selectedSituationFilter: OccurrenceSituation | 'all';
    policeReportFilter: string;
    teamsIds: number[];
    selectedDisplaceReasonId: number[];
    // region
    selectedPolygonType: string;
    selectedMapPolygonsIds: number[];
    // localização
    pin: LatLng | undefined;
    radius: number;
    withDispatch: boolean;
    withoutDispatch: boolean;
    withOccurrenceDocument: boolean;
    withoutOccurrenceDocument: boolean;
    selectedCameraTagsIds: number[];
    selectedFinishReasonCategoryIds: number[];
    selectedFinishReasonSubCategoryIds: number[];
    attachmentTypes: AttachmentType | 'all';
    involvedPersonSearch: string;
    handcuffUsed: boolean;
    arrested: boolean;
}

export default function ReportModal({ onClose, onApplyFilters, report, canDownloadReport, downloadReport }: Props) {
    const { styles, theme } = useStyles(styleSheet);
    const { user: authenticatedUser } = useAuth();

    const defaultEmptyValues: ReportModalForm = {
        period: { from: new Date(), to: new Date() },
        name: '',
        reportFormat: 'xlsx',
        tags: [],
        actingBodyId: 0,
        assumedBy: 0,
        receivedBy: 0,
        transferredBy: 0,
        finishedBy: 0,
        actingBodyCommandIds: [],
        triggerTypes: [],
        occurrenceTypes: [],
        occurrenceTagsIds: [],
        unitsIds: [],
        priority: 'all',
        selectedSituationFilter: 'all',
        policeReportFilter: '',
        teamsIds: [],
        selectedDisplaceReasonId: [],
        selectedPolygonType: '',
        selectedMapPolygonsIds: [],
        pin: undefined,
        radius: 1,
        withDispatch: false,
        withoutDispatch: false,
        withOccurrenceDocument: false,
        withoutOccurrenceDocument: false,
        selectedCameraTagsIds: [],
        selectedFinishReasonCategoryIds: [],
        selectedFinishReasonSubCategoryIds: [],
        attachmentTypes: 'all',
        involvedPersonSearch: '',
        handcuffUsed: false,
        arrested: false,
    };
    const [openFilter, setOpenFilter] = useState<string | null>('actingBody');
    const [polygonPendingLoading, setPolygonPendingLoading] = useState<boolean>(false);
    const [address, setAddress] = useState('');
    const [fileFormatZIndex, setFileFormatZIndex] = useState<number>(2);
    const [expandedFinishCategory, setExpandedFinishCategory] = useState<number>(0);

    const form = useForm<ReportModalForm>({
        defaultValues: defaultEmptyValues,
        mode: 'onChange'
    });

    function resetState() {
        form.reset(defaultEmptyValues);
        setOpenFilter('actingBody');
        setAddress('');
        setExpandedFinishCategory(0);
    }

    async function formSubmit() {
        try {
            const values = form.getValues();

            values.period.to!.setHours(23, 59, 59);
            values.period.from!.setHours(0, 0, 0);

            await dispatchService.createReport({
                name: values.name,
                isPdf: values.reportFormat == 'pdf',
                isCsv: values.reportFormat == 'csv',
                startDate: moment(values.period.from).format('YYYY-MM-DD'),
                endDate: moment(values.period.to).format('YYYY-MM-DD'),
                tags: values.tags,
                actingBodyId: values.actingBodyId ? values.actingBodyId : undefined,
                assumedBy: values.assumedBy ? values.assumedBy : undefined,
                transferredBy: values.transferredBy ? values.transferredBy : undefined,
                receivedBy: values.receivedBy ? values.receivedBy : undefined,
                finishedBy: values.finishedBy ? values.finishedBy : undefined,
                actingBodyCommandIds: values.actingBodyCommandIds,
                occurrenceTypesIds: values.occurrenceTypes,
                triggerTypeIds: values.triggerTypes,
                occurrenceTagsIds: values.occurrenceTagsIds,
                unitNamesIds: values.unitsIds,
                priority: values.priority !== 'all' ? values.priority : undefined,
                situation: values.selectedSituationFilter !== 'all' ? values.selectedSituationFilter : undefined,
                policeReport: values.policeReportFilter ? values.policeReportFilter : undefined,
                displaceReasonIds: values.selectedDisplaceReasonId.length ? values.selectedDisplaceReasonId : undefined,
                actingBodyUnitIds: values.teamsIds,
                mapPolygonIds: values.selectedMapPolygonsIds,
                latitude: values.pin?.lat,
                longitude: values.pin?.lng,
                radius: values.radius,
                withDispatch: values.withDispatch,
                withoutDispatch: values.withoutDispatch,
                withOccurrenceDocument: values.withOccurrenceDocument,
                withoutOccurrenceDocument: values.withoutOccurrenceDocument,
                cameraTags: values.selectedCameraTagsIds,
                finishReasonCategoryIds: values.selectedFinishReasonCategoryIds,
                finishReasonSubCategoryIds: values.selectedFinishReasonSubCategoryIds,
                attachmentTypes: values.attachmentTypes !== 'all' ? [values.attachmentTypes] : undefined,
                involvedPersonSearch: values.involvedPersonSearch !== '' ? values.involvedPersonSearch : undefined,
                handcuffUsed: values.handcuffUsed ? values.handcuffUsed : undefined,
                arrested: values.arrested ? values.arrested : undefined,
            });

            Toast.show({
                type: 'sentinelxSuccess',
                text1: translate('ActionSuccessfully'),
            });

            resetState();
            onApplyFilters();
        } catch (err) {
            console.error(err);
            Toast.show({
                type: 'sentinelxError',
                text1: translate('unexpectedError'),
            });
        }
    }

    function getActingBodyId() {
        if (!authenticatedUser || !authenticatedUser.actingBodyUnit.actingBodyId) {
            return 0;
        }

        if (authenticatedUser.isAdmin || authenticatedUser.permissions.dispatch_manage_all_acting_bodies_reports) {
            if (form.getValues().actingBodyId) {
                return form.getValues().actingBodyId;
            }
            return 0;
        } else {
            return authenticatedUser.actingBodyUnit.actingBodyId;
        }
    }

    const values = useWatch({ control: form.control });
    const needsActingBody = getActingBodyId() === 0;

    const isViewMode = !!report;
    const totalActiveFilters = isViewMode
        ? [
            report.actingBodyId ? 1 : 0,
            report.filters.assumedBy ? 1 : 0,
            report.filters.receivedBy ? 1 : 0,
            report.filters.transferredBy ? 1 : 0,
            report.filters.finishedBy ? 1 : 0,
            (report.filters.actingBodyCommands?.length ?? 0) > 0 ? 1 : 0,
            (report.filters.triggerTypes?.length ?? 0) > 0 ? 1 : 0,
            (report.filters.occurrenceTypes?.length ?? 0) > 0 ? 1 : 0,
            (report.filters.occurrenceTags?.length ?? 0) > 0 ? 1 : 0,
            (report.filters.unitNames?.length ?? 0) > 0 ? 1 : 0,
            report.filters.priority && report.filters.priority !== 'all' ? 1 : 0,
            report.filters.situation && report.filters.situation !== 'all' ? 1 : 0,
            (report.filters.attachmentTypes?.length ?? 0) > 0 ? 1 : 0,
            report.filters.policeReport ? 1 : 0,
            (report.filters.actingBodyUnits?.length ?? 0) > 0 ? 1 : 0,
            (report.filters.displaceReasons?.length ?? 0) > 0 ? 1 : 0,
            (report.filters.mapPolygons?.length ?? 0) > 0 ? 1 : 0,
            (report.filters.latitude && report.filters.longitude && report.filters.radius) ? 1 : 0,
            (report.filters.withDispatch || report.filters.withoutDispatch) ? 1 : 0,
            (report.filters.withOccurrenceDocument || report.filters.withoutOccurrenceDocument) ? 1 : 0,
            (report.filters.cameraTagsNames?.length ?? 0) > 0 ? 1 : 0,
            (report.filters.finishReasonCategories?.length ?? 0) > 0 ? 1 : 0,
            (report.filters.finishReasonSubCategories?.length ?? 0) > 0 ? 1 : 0,
            (report.filters.involvedPersonSearch || report.filters.handcuffUsed || report.filters.arrested) ? 1 : 0,
        ].reduce((a, b) => a + b, 0)
        : [
            values.actingBodyId !== 0 ? 1 : 0,
            values.assumedBy !== 0 ? 1 : 0,
            values.receivedBy !== 0 ? 1 : 0,
            values.transferredBy !== 0 ? 1 : 0,
            values.finishedBy !== 0 ? 1 : 0,
            (values.actingBodyCommandIds?.length ?? 0) > 0 ? 1 : 0,
            (values.triggerTypes?.length ?? 0) > 0 ? 1 : 0,
            (values.occurrenceTypes?.length ?? 0) > 0 ? 1 : 0,
            (values.occurrenceTagsIds?.length ?? 0) > 0 ? 1 : 0,
            (values.unitsIds?.length ?? 0) > 0 ? 1 : 0,
            values.priority !== 'all' ? 1 : 0,
            values.selectedSituationFilter !== 'all' ? 1 : 0,
            values.attachmentTypes !== 'all' ? 1 : 0,
            values.policeReportFilter ? 1 : 0,
            (values.teamsIds?.length ?? 0) > 0 ? 1 : 0,
            (values.selectedDisplaceReasonId?.length ?? 0) > 0 ? 1 : 0,
            values.selectedPolygonType ? 1 : 0,
            (values.selectedMapPolygonsIds?.length ?? 0) > 0 ? 1 : 0,
            values.pin && values.radius ? 1 : 0,
            values.withDispatch ? 1 : 0,
            values.withoutDispatch ? 1 : 0,
            values.withOccurrenceDocument ? 1 : 0,
            values.withoutOccurrenceDocument ? 1 : 0,
            (values.selectedCameraTagsIds?.length ?? 0) > 0 ? 1 : 0,
            (values.selectedFinishReasonCategoryIds?.length ?? 0) > 0 ? 1 : 0,
            (values.selectedFinishReasonSubCategoryIds?.length ?? 0) > 0 ? 1 : 0,
            (values.involvedPersonSearch || values.handcuffUsed || values.arrested) ? 1 : 0,
        ].reduce((a, b) => a + b, 0);

    useEffect(() => {
        if (!isViewMode && values.actingBodyId) {
            form.reset({
                ...values,
                teamsIds: [],
                actingBodyCommandIds: [],
                occurrenceTagsIds: [],
                unitsIds: [],
                occurrenceTypes: [],
                assumedBy: 0,
                finishedBy: 0,
                receivedBy: 0,
                transferredBy: 0,
                selectedFinishReasonCategoryIds: [],
                selectedFinishReasonSubCategoryIds: [],
            });
        }

    }, [values.actingBodyId]);

    const toggleFilter = (key: string) => {
        setOpenFilter(prev => prev === key ? null : key);
    };

    const requiresActingBody = authenticatedUser?.isAdmin || authenticatedUser?.permissions.dispatch_manage_all_acting_bodies_reports;
    const filtersRef = useRef<FiltersRef>(null);

    useEffect(() => {
        if (isViewMode) {
            form.reset({
                name: report.name,
                period: { from: moment(report.filters.startDate).toDate(), to: moment(report.filters.endDate).toDate() },
                reportFormat: report.filters.isPdf ? 'pdf' : report.filters.isCsv ? 'csv' : 'xlsx',
                pin: report.filters.latitude && report.filters.longitude ? { lat: report.filters.latitude, lng: report.filters.longitude } : undefined,
                radius: report.filters.radius
            });

            const getAddress = async () => {
                if (report.filters.latitude && report.filters.longitude) {
                    const geocoder = new google.maps.Geocoder();
                    const data = await geocoder.geocode({ location: { lat: report.filters.latitude, lng: report.filters.longitude } });
                    setAddress(data.results[0].formatted_address || '');
                }
            };

            getAddress();
            filtersRef.current?.open();
        }
    }, [isViewMode]);

    return (
        <Filters
            ref={filtersRef}
            onCleanFilters={resetState}
            onFilters={() => {
                if (isViewMode) {
                    downloadReport?.();
                } else {
                    formSubmit();
                }
            }}
            onStatusChange={(isOpen) => {
                if (!isOpen) {
                    resetState();
                    onClose();
                }
            }}
            applyIsLoading={polygonPendingLoading}
            applyDisabled={isViewMode
                ? !canDownloadReport
                : (!!values.selectedPolygonType && !values.selectedMapPolygonsIds?.length) ||
                !form.formState.isValid ||
                (requiresActingBody && !values.actingBodyId)
            }
            cleanDisabled={isViewMode}
            filterCount={totalActiveFilters}
            btnContent={<>
                <MyAppText style={{ fontSize: 16, fontWeight: '600', color: theme.colors.primaryButton.text }}>{translate('generateReport')}</MyAppText>
            </>}
            applyText={isViewMode ? translate('downloadReport') : translate('generateReport')}
            cancelText={translate('cleanSelection')}
            headerTitle={translate('occurrenceReport')}
            subheaderTitle={translate('occurrenceReportInformation')}
            primaryButton
        >
            <FilterSection
                sectionTitle={translate('reportData')}
                sectionTitleStyle={{ paddingTop: 0 }}
            >
                <View style={{ flexDirection: 'column', gap: 16, zIndex: 2 }}>
                    <FormInput
                        form={form}
                        fieldName='name'
                        label={translate('name')}
                        placeholder={translate('name')}
                        required
                        disabled={isViewMode}
                    />
                    <MyDateRangePicker
                        label={translate('period')}
                        maxDate={moment().toDate()}
                        form={form}
                        fieldName='period'
                        zIndex={9}
                        required={true}
                        disabled={isViewMode}
                    />
                    <View style={{ flexDirection: 'row', zIndex: fileFormatZIndex }}>
                        <FileFormatDropdown
                            form={form}
                            fieldName='reportFormat'
                            zIndex={10}
                            required={true}
                            includesXlsx={true}
                            onOpen={() => setFileFormatZIndex(10000)}
                            onClose={() => setFileFormatZIndex(2)}
                            disabled={isViewMode}
                        />
                        <View style={{ flex: 2 }} />
                    </View>

                    <CollapsibleFilter
                        label={translate('tag')}
                        isOpen={openFilter === 'tags'}
                        onToggle={() => toggleFilter('tags')}
                        count={isViewMode
                            ? report.tags.length
                            : values.tags?.length
                        }
                    >
                        {isViewMode
                            ? <SelectedItemsList labels={report.tags.map(t => t.name)} renderBadge variant='checkbox' />
                            : needsActingBody
                                ? <FilterDependencyMessage message={translate('selectActingBodyFirst')} />
                                : <TagsDropdown
                                    actingBodyId={getActingBodyId()}
                                    value={values.tags!}
                                    setValue={makeSetStateHandler(form, 'tags')}
                                    zIndex={1}
                                    useNewStyle
                                />
                        }
                    </CollapsibleFilter>
                </View>
            </FilterSection>

            <FilterSection sectionTitle={translate('organizational')}>
                <View style={{ zIndex: 1, gap: 16 }}>
                    {(authenticatedUser?.isAdmin || authenticatedUser?.permissions.dispatch_manage_all_acting_bodies_reports) &&
                        <CollapsibleFilter
                            label={translate('actingBody')}
                            isOpen={openFilter === 'actingBody'}
                            onToggle={() => toggleFilter('actingBody')}
                            count={isViewMode
                                ? report.actingBody ? 1 : 0
                                : values.actingBodyId !== 0 ? 1 : 0
                            }
                            required={(authenticatedUser?.isAdmin || authenticatedUser?.permissions.dispatch_manage_all_acting_bodies_reports)}
                        >
                            {isViewMode
                                ? <SelectedItemsList labels={[report.actingBody!.name]} />
                                : <Controller
                                    control={form.control}
                                    name="actingBodyId"
                                    rules={{
                                        required: translate('required_field'),
                                        validate: (value) => value !== 0 || translate('required_field')
                                    }}
                                    render={({ field, fieldState }) => (
                                        <>
                                            <ActingBodiesList
                                                value={field.value}
                                                setValue={makeSetStateHandler(form, 'actingBodyId')}
                                            />
                                            {fieldState.error ? <MyAppText style={styles.error}>{fieldState.error.message}</MyAppText> : null}
                                        </>
                                    )}
                                />
                            }
                        </CollapsibleFilter>
                    }

                    <CollapsibleFilter
                        label={translate('actingBodyUnit')}
                        isOpen={openFilter === 'unitNames'}
                        onToggle={() => toggleFilter('unitNames')}
                        count={isViewMode
                            ? report.filters.actingBodyUnits?.length
                            : values.teamsIds?.length
                        }
                    >
                        {isViewMode
                            ? <SelectedItemsList labels={report.filters.actingBodyUnits || []} variant='checkbox' />
                            : needsActingBody
                                ? <FilterDependencyMessage message={translate('selectActingBodyFirst')} />
                                : <ActingBodyUnitsList
                                    value={values.teamsIds!}
                                    setValue={makeSetStateHandler(form, 'teamsIds')}
                                    actingBodyId={getActingBodyId()}
                                />
                        }
                    </CollapsibleFilter>

                    <CollapsibleFilter
                        label={translate('command')}
                        isOpen={openFilter === 'actingBodyCommands'}
                        onToggle={() => toggleFilter('actingBodyCommands')}
                        count={isViewMode ? report.filters.actingBodyCommands?.length : values.actingBodyCommandIds?.length}
                    >
                        {isViewMode
                            ? <SelectedItemsList labels={report.filters.actingBodyCommands || []} variant='checkbox' />
                            : needsActingBody
                                ? <FilterDependencyMessage message={translate('selectActingBodyFirst')} />
                                : <DispatchActingBodiesCommandsList
                                    value={values.actingBodyCommandIds!}
                                    actingBodyId={getActingBodyId()}
                                    setValue={makeSetStateHandler(form, 'actingBodyCommandIds')}
                                />
                        }
                    </CollapsibleFilter>
                </View>
            </FilterSection>

            <FilterSection sectionTitle={translate('occurrenceData')}>
                <CollapsibleFilter
                    label={translate('priority')}
                    isOpen={openFilter === 'priority'}
                    onToggle={() => toggleFilter('priority')}
                    count={isViewMode
                        ? report.filters.priority && report.filters.priority !== 'all' ? 1 : 0
                        : values.priority !== 'all' ? 1 : 0
                    }
                >
                    {isViewMode
                        ? <SelectedItemsList labels={report.filters.priority ? [translate(report.filters.priority)] : []} />
                        : <PriorityList
                            value={values.priority!}
                            setValue={makeSetStateHandler(form, 'priority')}
                        />
                    }
                </CollapsibleFilter>

                <CollapsibleFilter
                    label={translate('status')}
                    isOpen={openFilter === 'status'}
                    onToggle={() => toggleFilter('status')}
                    count={isViewMode
                        ? report.filters.situation && report.filters.situation !== 'all' ? 1 : 0
                        : values.selectedSituationFilter !== 'all' ? 1 : 0
                    }
                >
                    {isViewMode
                        ? <SelectedItemsList labels={report.filters.situation ? [translate(report.filters.situation)] : []} />
                        : <StatusList
                            value={values.selectedSituationFilter!}
                            setValue={makeSetStateHandler(form, 'selectedSituationFilter')}
                        />
                    }
                </CollapsibleFilter>

                <CollapsibleFilter
                    label={translate('tag')}
                    isOpen={openFilter === 'occurrenceTags'}
                    onToggle={() => toggleFilter('occurrenceTags')}
                    count={isViewMode ? report.filters.occurrenceTags?.length : values.occurrenceTagsIds?.length}
                >
                    {isViewMode
                        ? <SelectedItemsList labels={report.filters.occurrenceTags || []} variant='checkbox' renderBadge />
                        : needsActingBody
                            ? <FilterDependencyMessage message={translate('selectActingBodyFirst')} />
                            : <TagsList
                                value={values.occurrenceTagsIds!}
                                setValue={makeSetStateHandler(form, 'occurrenceTagsIds')}
                                actingBodyId={getActingBodyId()}
                            />
                    }
                </CollapsibleFilter>

                <CollapsibleFilter
                    label={translate('policeReport')}
                    isOpen={openFilter === 'policeReport'}
                    onToggle={() => toggleFilter('policeReport')}
                    count={isViewMode ? report.filters.policeReport ? 1 : 0 : values.policeReportFilter ? 1 : 0}
                    searchComponent={<FilterInput
                        placeholder={translate('typeToSearch')}
                        value={isViewMode ? report.filters.policeReport || '' : values.policeReportFilter!}
                        onChange={makeSetStateHandler(form, 'policeReportFilter')}
                        disabled={isViewMode}
                    />}
                />
            </FilterSection>

            <FilterSection sectionTitle={translate('teams')}>
                <CollapsibleFilter
                    label={translate('team')}
                    isOpen={openFilter === 'teams'}
                    onToggle={() => toggleFilter('teams')}
                    count={isViewMode ? report.filters.unitNames?.length : values.unitsIds?.length}
                >
                    {isViewMode
                        ? <SelectedItemsList labels={report.filters.unitNames || []} variant='checkbox' />
                        : needsActingBody
                            ? <FilterDependencyMessage message={translate('selectActingBodyFirst')} />
                            : <DispatchUnitNamesList
                                value={values.unitsIds!}
                                setValue={makeSetStateHandler(form, 'unitsIds')}
                                actingBodyId={getActingBodyId()}
                            />
                    }
                </CollapsibleFilter>

                <CollapsibleFilter
                    label={translate('displaced')}
                    isOpen={openFilter === 'displaceReasons'}
                    onToggle={() => toggleFilter('displaceReasons')}
                    count={isViewMode ? report.filters.displaceReasons?.length : values.selectedDisplaceReasonId?.length}
                >
                    {isViewMode
                        ? <SelectedItemsList labels={report.filters.displaceReasons || []} variant='checkbox' />
                        : <DispatchDisplacedReasonsList
                            value={values.selectedDisplaceReasonId!}
                            setValue={makeSetStateHandler(form, 'selectedDisplaceReasonId')}
                        />
                    }
                </CollapsibleFilter>
            </FilterSection>

            <FilterSection sectionTitle={translate('additionalInformation')}>
                <CollapsibleFilter
                    label={translate('attachment')}
                    isOpen={openFilter === 'attachment'}
                    onToggle={() => toggleFilter('attachment')}
                    count={isViewMode ? report.filters.attachmentTypes?.length : values.attachmentTypes !== 'all' ? 1 : 0}
                >
                    {isViewMode
                        ? <SelectedItemsList labels={report?.filters.attachmentTypes?.map(type => translate(`attachment${(type as string).charAt(0).toUpperCase() + (type as string).slice(1)}`)) || []} />
                        : <AttachmentTypesList value={values.attachmentTypes!} setValue={makeSetStateHandler(form, 'attachmentTypes')} />
                    }
                </CollapsibleFilter>

                <CollapsibleFilter
                    label={translate('involvedPerson')}
                    isOpen={openFilter === 'involvedPerson'}
                    onToggle={() => toggleFilter('involvedPerson')}
                    count={isViewMode
                        ? (report.filters.involvedPersonSearch ? 1 : 0) + (report.filters.handcuffUsed ? 1 : 0) + (report.filters.arrested ? 1 : 0)
                        : (values.involvedPersonSearch ? 1 : 0) + (values.handcuffUsed ? 1 : 0) + (values.arrested ? 1 : 0)
                    }
                >
                    {isViewMode
                        ? <SelectedItemsList
                            labels={[report.filters.handcuffUsed ? translate('handcuffUsed') : '', report.filters.arrested ? translate('arrested') : ''].filter(Boolean)}
                            variant='checkbox'
                            textualSearch={report.filters.involvedPersonSearch}
                        />
                        : <InvolvedPeopleList
                            search={values.involvedPersonSearch ?? ''}
                            setSearch={makeSetStateHandler(form, 'involvedPersonSearch')}
                            handcuffUsed={values.handcuffUsed ?? false}
                            setHandcuffUsed={makeSetStateHandler(form, 'handcuffUsed')}
                            arrested={values.arrested ?? false}
                            setArrested={makeSetStateHandler(form, 'arrested')}
                        />
                    }
                </CollapsibleFilter>
            </FilterSection>

            <FilterSection sectionTitle={translate('location')}>
                <CollapsibleFilter
                    label={translate('region')}
                    isOpen={openFilter === 'regions'}
                    onToggle={() => toggleFilter('regions')}
                    count={isViewMode
                        ? report.filters.mapPolygons?.length
                        : (values.selectedPolygonType ? 1 : 0) + (values.selectedMapPolygonsIds && values.selectedMapPolygonsIds?.length > 0 ? 1 : 0)
                    }
                >
                    {isViewMode
                        ? <SelectedItemsList groupedLabels={report.filters.mapPolygonsByType} />
                        : <DispatchRegionsList
                            multiple={true}
                            polygonType={values.selectedPolygonType!}
                            setPolygonType={makeSetStateHandler(form, 'selectedPolygonType')}
                            mapPolygonsIds={values.selectedMapPolygonsIds!}
                            setMapPolygonsIds={makeSetStateHandler(form, 'selectedMapPolygonsIds')}
                            setPendingLoading={setPolygonPendingLoading}
                        />
                    }
                </CollapsibleFilter>

                <CollapsibleFilter
                    label={translate('findByLocation')}
                    isOpen={openFilter === 'findByLocation'}
                    onToggle={() => toggleFilter('findByLocation')}
                    count={values.pin ? 1 : 0}
                    searchComponent={
                        isViewMode
                            ? values.pin
                                ? undefined
                                : <FilterDependencyMessage message={translate('filterNotUsedForReport')} />
                            : values.pin ? (
                                <Tooltip
                                    useSimpleBox
                                    tooltipContent={translate('onlyOneAddressAtATime')}
                                    tooltipDirection='top'
                                    style={{
                                        color: theme.colors.white['500'],
                                        padding: 16,
                                        borderRadius: 12,
                                        width: 200,
                                        justifyContent: 'center',
                                        zIndex: 9999,
                                        background: theme.colors.neutral['500'],
                                    }}
                                >
                                    <LocationSelector
                                        address={address}
                                        onAddressChange={setAddress}
                                        onPinChange={makeSetStateHandler(form, 'pin')}
                                        disabled
                                    />
                                </Tooltip>
                            ) : (
                                <LocationSelector address={address} onAddressChange={setAddress} onPinChange={makeSetStateHandler(form, 'pin')} />
                            )
                    }
                >
                    <View style={{ gap: 12 }}>
                        {values.pin && (
                            <>
                                <View style={styles.headerRow}>
                                    <MyAppText style={styles.headerLabel}>{translate('selectedAddress')}</MyAppText>
                                    {!isViewMode && <TouchableOpacity onPress={() => {
                                        form.setValue('pin', undefined);
                                        setAddress('');
                                    }}>
                                        <MyAppText style={styles.clearSelection}>{translate('clearSelection')}</MyAppText>
                                    </TouchableOpacity>
                                    }
                                </View>

                                <View style={styles.addressBadge}>
                                    <MyAppText style={styles.addressText}>{address}</MyAppText>
                                    {!isViewMode && <TouchableOpacity onPress={() => {
                                        form.setValue('pin', undefined);
                                        setAddress('');
                                    }}>
                                        <FontAwesomeIcon icon={faXmark as IconDefinition} fontSize={14} color={theme.colors.labelColor} />
                                    </TouchableOpacity>
                                    }
                                </View>

                                <View style={{ justifyContent: 'center', alignItems: 'center', flexDirection: 'row' }}>
                                    <MyAppText style={{ fontWeight: '600' }}>{translate('radius')}</MyAppText>
                                    <MyAppText>{`: ${values.radius!.toFixed(2)}km`}</MyAppText>
                                </View>
                                <Slider
                                    disabled={isViewMode}
                                    animateTransitions
                                    minimumValue={0.1}
                                    maximumValue={3}
                                    minimumTrackTintColor={theme.colors.primary['500']}
                                    renderThumbComponent={() => (
                                        <View style={styles.thumb} />
                                    )}
                                    maximumTrackTintColor={theme.colors.primary['200']}
                                    value={values.radius!}
                                    step={0.1}
                                    onValueChange={(value) => form.setValue('radius', value[0])}
                                />
                                <View style={{ width: '100%', height: 450 }}>
                                    <Map pinnedMarker={{ lat: values.pin?.lat || 0, lng: values.pin?.lng || 0 }} draggable={true} mapControls={false} radius={values.radius} />
                                </View>
                            </>
                        )}
                    </View>
                </CollapsibleFilter>
            </FilterSection>

            <FilterSection sectionTitle={translate('classification')} >
                <CollapsibleFilter
                    label={translate('dispatchInOccurrence')}
                    isOpen={openFilter === 'withDispatch'}
                    onToggle={() => toggleFilter('withDispatch')}
                    count={
                        isViewMode
                            ? (report.filters.withDispatch || report.filters.withoutDispatch) ? 1 : 0
                            : (values.withDispatch || values.withoutDispatch) ? 1 : 0
                    }
                >
                    {isViewMode
                        ? <SelectedItemsList
                            labels={[report.filters.withDispatch ? translate('withTeamDispatch') : '', report.filters.withoutDispatch ? translate('withoutTeamDispatch') : ''].filter(Boolean)}
                        />
                        : <FilterRadioList
                            items={[
                                { label: translate('withTeamDispatch'), value: 'yes' },
                                { label: translate('withoutTeamDispatch'), value: 'no' },
                            ]}
                            value={values.withDispatch ? 'yes' : values.withoutDispatch ? 'no' : 'all'}
                            setValue={(value) => {
                                form.setValue('withDispatch', value === 'yes');
                                form.setValue('withoutDispatch', value === 'no');
                            }}
                            clearValue='all'
                        />
                    }
                </CollapsibleFilter>

                <CollapsibleFilter
                    label={translate('occurrenceDocument')}
                    isOpen={openFilter === 'withOccurrenceDocument'}
                    onToggle={() => toggleFilter('withOccurrenceDocument')}
                    count={
                        isViewMode
                            ? (report.filters.withOccurrenceDocument || report.filters.withoutOccurrenceDocument) ? 1 : 0
                            : (values.withOccurrenceDocument || values.withoutOccurrenceDocument) ? 1 : 0
                    }
                >
                    {isViewMode
                        ? <SelectedItemsList
                            labels={[report.filters.withOccurrenceDocument ? translate('withOccurrenceDocumentGenerated') : '', report.filters.withoutOccurrenceDocument ? translate('withoutOccurrenceDocumentGenerated') : ''].filter(Boolean)}
                        />
                        : <FilterRadioList
                            items={[
                                { label: translate('withOccurrenceDocumentGenerated'), value: 'yes' },
                                { label: translate('withoutOccurrenceDocumentGenerated'), value: 'no' },
                            ]}
                            value={values.withOccurrenceDocument ? 'yes' : values.withoutOccurrenceDocument ? 'no' : 'all'}
                            setValue={(value) => {
                                form.setValue('withOccurrenceDocument', value === 'yes');
                                form.setValue('withoutOccurrenceDocument', value === 'no');
                            }}
                            clearValue='all'
                        />
                    }
                </CollapsibleFilter>

                <CollapsibleFilter
                    label={translate('typeOfIntegration')}
                    isOpen={openFilter === 'type'}
                    onToggle={() => toggleFilter('type')}
                    count={isViewMode ? report.filters.triggerTypes?.length : values.triggerTypes?.length}
                >
                    {isViewMode
                        ? <SelectedItemsList labels={report.filters.triggerTypes?.map(trigger => translate(trigger)) || []} variant='checkbox' />
                        : <TypeDropdown
                            value={values.triggerTypes!}
                            setValue={makeSetStateHandler(form, 'triggerTypes')}
                        />
                    }
                </CollapsibleFilter>

                <CollapsibleFilter
                    label={translate('occurrenceType')}
                    isOpen={openFilter === 'occurrenceType'}
                    onToggle={() => toggleFilter('occurrenceType')}
                    count={isViewMode ? report.filters.occurrenceTypes?.length : values.occurrenceTypes?.length}
                >
                    {isViewMode
                        ? <SelectedItemsList labels={report.filters.occurrenceTypes || []} variant='checkbox' />
                        : needsActingBody
                            ? <FilterDependencyMessage message={translate('selectActingBodyFirst')} />
                            : <OccurrenceTypesList
                                value={values.occurrenceTypes!}
                                setValue={makeSetStateHandler(form, 'occurrenceTypes')}
                                actingBodyId={getActingBodyId()}
                            />
                    }
                </CollapsibleFilter>

                <CollapsibleFilter
                    label={translate('cameraTag')}
                    isOpen={openFilter === 'cameraTags'}
                    onToggle={() => toggleFilter('cameraTags')}
                    count={isViewMode ? report.filters.cameraTagsNames?.length : values.selectedCameraTagsIds?.length}
                >
                    {isViewMode
                        ? <SelectedItemsList labels={report.filters.cameraTagsNames || []} renderBadge variant='checkbox' />
                        : <CameraTagsList
                            value={values.selectedCameraTagsIds!}
                            setValue={makeSetStateHandler(form, 'selectedCameraTagsIds')}
                        />
                    }
                </CollapsibleFilter>
            </FilterSection>

            <FilterSection sectionTitle={translate('occurrenceActions')}>
                <CollapsibleFilter
                    label={translate('assumedBy')}
                    isOpen={openFilter === 'assumedBy'}
                    onToggle={() => toggleFilter('assumedBy')}
                    count={isViewMode
                        ? report.filters.assumedBy ? 1 : 0
                        : values.assumedBy !== 0 ? 1 : 0
                    }
                >
                    {isViewMode
                        ? <SelectedItemsList labels={report.filters.assumedBy ? [report.filters.assumedBy] : []} />
                        : needsActingBody
                            ? <FilterDependencyMessage message={translate('selectActingBodyFirst')} />
                            : <DispatchGenericUserList
                                value={values.assumedBy!}
                                setValue={makeSetStateHandler(form, 'assumedBy')}
                                actingBodyId={getActingBodyId()}
                                label='assumedBy'
                            />
                    }
                </CollapsibleFilter>

                <CollapsibleFilter
                    label={translate('finishedBy')}
                    isOpen={openFilter === 'finishedBy'}
                    onToggle={() => toggleFilter('finishedBy')}
                    count={isViewMode
                        ? report.filters.finishedBy ? 1 : 0
                        : values.finishedBy !== 0 ? 1 : 0
                    }
                >
                    {isViewMode
                        ? <SelectedItemsList labels={report.filters.finishedBy ? [report.filters.finishedBy] : []} />
                        : needsActingBody
                            ? <FilterDependencyMessage message={translate('selectActingBodyFirst')} />
                            : <DispatchGenericUserList
                                value={values.finishedBy!}
                                setValue={makeSetStateHandler(form, 'finishedBy')}
                                actingBodyId={getActingBodyId()}
                                label='finishedBy'
                            />
                    }
                </CollapsibleFilter>

                <CollapsibleFilter
                    label={translate('receivedBy')}
                    isOpen={openFilter === 'receivedBy'}
                    onToggle={() => toggleFilter('receivedBy')}
                    count={isViewMode
                        ? report.filters.receivedBy ? 1 : 0
                        : values.receivedBy !== 0 ? 1 : 0
                    }
                >
                    {isViewMode
                        ? <SelectedItemsList labels={report.filters.receivedBy ? [report.filters.receivedBy] : []} />
                        : needsActingBody
                            ? <FilterDependencyMessage message={translate('selectActingBodyFirst')} />
                            : <DispatchGenericUserList
                                value={values.receivedBy!}
                                setValue={makeSetStateHandler(form, 'receivedBy')}
                                actingBodyId={getActingBodyId()}
                                label='receivedBy'
                            />
                    }
                </CollapsibleFilter>

                <CollapsibleFilter
                    label={translate('transferredBy')}
                    isOpen={openFilter === 'transferredBy'}
                    onToggle={() => toggleFilter('transferredBy')}
                    count={isViewMode
                        ? report.filters.transferredBy ? 1 : 0
                        : values.transferredBy !== 0 ? 1 : 0
                    }
                >
                    {isViewMode
                        ? <SelectedItemsList labels={report.filters.transferredBy ? [report.filters.transferredBy] : []} />
                        : needsActingBody
                            ? <FilterDependencyMessage message={translate('selectActingBodyFirst')} />
                            : <DispatchGenericUserList
                                value={values.transferredBy!}
                                setValue={makeSetStateHandler(form, 'transferredBy')}
                                actingBodyId={getActingBodyId()}
                                label='transferredBy'
                            />
                    }
                </CollapsibleFilter>

                <CollapsibleFilter
                    label={translate('finishReason')}
                    isOpen={openFilter === 'finishReason'}
                    onToggle={() => toggleFilter('finishReason')}
                    count={isViewMode ? report.filters.finishReasonCategories?.length : values.selectedFinishReasonCategoryIds?.length}
                >
                    {isViewMode
                        ? <SelectedItemsList labels={report.filters.finishReasonCategories || []} variant='checkbox' />
                        : needsActingBody
                            ? <FilterDependencyMessage message={translate('selectActingBodyFirst')} />
                            : <FinishReasonList
                                value={values.selectedFinishReasonCategoryIds!}
                                setValue={makeSetStateHandler(form, 'selectedFinishReasonCategoryIds')}
                                actingBodyId={getActingBodyId()}
                                onSelectCategory={() => {
                                    form.setValue('selectedFinishReasonSubCategoryIds', []);
                                }}
                            />
                    }
                </CollapsibleFilter>

                <CollapsibleFilter
                    label={translate('finishReasonSubCategory')}
                    isOpen={openFilter === 'finishReasonSubCategory'}
                    onToggle={() => toggleFilter('finishReasonSubCategory')}
                    count={isViewMode ? report.filters.finishReasonSubCategories?.length : values.selectedFinishReasonSubCategoryIds?.length}
                >
                    {isViewMode
                        ? <SelectedItemsList groupedLabels={report.filters.finishReasonSubCategoriesGrouped} />
                        : needsActingBody
                            ? <FilterDependencyMessage message={translate('selectActingBodyFirst')} />
                            : <FinishReasonSubCategoryList
                                value={values.selectedFinishReasonSubCategoryIds!}
                                setValue={makeSetStateHandler(form, 'selectedFinishReasonSubCategoryIds')}
                                actingBodyId={getActingBodyId()}
                                expandedCategory={expandedFinishCategory}
                                setExpandedCategory={setExpandedFinishCategory}
                                onSelectSubCategory={() => {
                                    form.setValue('selectedFinishReasonCategoryIds', []);
                                }}
                            />
                    }
                </CollapsibleFilter>
            </FilterSection>
        </Filters >
    );
}

const styleSheet = createStyleSheet((theme) => ({
    headerRow: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
    },
    headerLabel: {
        fontSize: 16,
        fontWeight: '600',
        color: theme.colors.labelColor,
    },
    clearSelection: {
        fontSize: 14,
        fontWeight: 'bold',
        color: theme.colors.primary['500'],
    },
    addressBadge: {
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 8,
        backgroundColor: theme.colors.primary['200'],
        paddingHorizontal: 16,
        paddingVertical: 10,
        borderRadius: 20,
    },
    addressText: {
        flex: 1,
        fontSize: 14,
        color: theme.colors.labelColor,
    },
    thumb: {
        width: 20,
        height: 20,
        borderRadius: 10,
        backgroundColor: theme.colors.white['500'],
        borderWidth: 5,
        borderColor: theme.colors.primary['500'],
    },
    error: {
        color: theme.colors.errors['300'],
        fontSize: 10
    },
}));
